import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { 
    ApplicationMetadata, 
    APPLICATIONS_CONFIG, 
    getApplicationConfig, 
    getApplicationVersion 
} from '../config/applicationsConfig';

export type ApplicationName = 'Planning' | 'Maker' | 'Parc' | 'Stock' | 'Academy' | 'CRM' | 'ProjectManagement';

interface ApplicationContextType {
    currentApp: ApplicationName | null;
    currentAppVersion: string;
    currentAppInfo: ApplicationMetadata | null;
    setApp: (app: ApplicationName) => void;
    isValidApp: (app: string) => boolean;
    getAppVersion: (app?: string) => string;
    getAppConfig: (app?: string) => ApplicationMetadata | null;
}

const ApplicationContext = createContext<ApplicationContextType | undefined>(undefined);

export const useApplication = () => {
    const context = useContext(ApplicationContext);
    if (!context) {
        throw new Error('useApplication must be used within an ApplicationProvider');
    }
    return context;
};

export const ApplicationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentApp, setCurrentApp] = useState<ApplicationName | null>(null);

    const validApps: ApplicationName[] = ['Planning', 'Maker', 'Parc', 'Stock', 'Academy', 'CRM', 'ProjectManagement'];

    const isValidApp = (app: string): boolean => {
        return validApps.includes(app as ApplicationName);
    };

    const setApp = (app: ApplicationName) => {
        if (isValidApp(app)) {
            setCurrentApp(app);
            // Optionally persist to localStorage if needed
            localStorage.setItem('currentApp', app);
        }
    };

    // Try to restore from localStorage on mount if not set (optional, but good for UX if URL doesn't have it)
    useEffect(() => {
        const stored = localStorage.getItem('currentApp');
        if (stored && isValidApp(stored) && !currentApp) {
            setCurrentApp(stored as ApplicationName);
        }
    }, []);

    const currentAppInfo = getApplicationConfig(currentApp);
    const currentAppVersion = currentAppInfo ? currentAppInfo.versionTag : '';

    const getAppVersion = (app?: string): string => {
        return getApplicationVersion(app || currentApp || undefined);
    };

    const getAppConfig = (app?: string): ApplicationMetadata | null => {
        return getApplicationConfig(app || currentApp || undefined);
    };

    return (
        <ApplicationContext.Provider value={{ 
            currentApp, 
            currentAppVersion,
            currentAppInfo,
            setApp, 
            isValidApp,
            getAppVersion,
            getAppConfig
        }}>
            {children}
        </ApplicationContext.Provider>
    );
};
